import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Copy,
  CheckCircle,
  AlertTriangle,
  Clock,
  DollarSign,
  Target,
  Users,
  FileText,
  Workflow,
  Layers,
  Zap,
  Shield,
  BarChart3,
  Calendar,
  Tag,
  Link,
  Download,
  Upload,
  Search,
  Filter,
  MoreVertical
} from 'lucide-react';
import MasterDataService from '../../services/masterDataService';
import { useToast, ToastContainer } from '../common/Toast';

const ContractTypeConfiguration = () => {
  const navigate = useNavigate();
  const { id: contractTypeId } = useParams();
  const searchParams = new URLSearchParams(window.location.search);
  const mode = searchParams.get('mode') || 'edit'; // 'view' or 'edit'
  const isViewMode = mode === 'view';
  const { success, error: showError, toasts, removeToast } = useToast();
  
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state for contract type configuration
  const [formData, setFormData] = useState({
    // Basic Information
    id: contractTypeId || null,
    name: '',
    description: '',
    category: '',
    type: '',
    status: 'active',
    priority: 'Medium',
    
    // Pricing Model Configuration
    pricingModel: 'Fixed-Price',
    baseAmount: '',
    currency: 'USD',
    hourlyRate: '',
    materialMarkup: '',
    billingFrequency: 'Monthly',
    
    // Fixed-Price Specific Configuration
    fixedPrice: {
      scope: [],
      deliverables: [],
      milestones: [],
      qualityGates: [],
      changeManagement: {
        enabled: true,
        approvalRequired: true,
        maxChanges: 5,
        changeFee: 0
      },
      performanceMetrics: {
        qualityScore: 0,
        timelinessScore: 0,
        customerSatisfaction: 0
      }
    },
    
    // Time & Materials Specific Configuration
    timeMaterials: {
      hourlyRates: [],
      materialCosts: [],
      timeTracking: {
        enabled: true,
        required: true,
        approvalRequired: true,
        maxHoursPerDay: 8,
        overtimeAllowed: false
      },
      costTracking: {
        enabled: true,
        categories: [],
        budgetLimits: [],
        costCeiling: 0
      },
      billingRules: {
        minimumHours: 0,
        roundingRules: 'quarter-hour',
        travelTime: false,
        administrativeTime: false
      }
    },
    
    // Contract Terms
    duration: '',
    autoRenewal: false,
    renewalTerms: '',
    noticePeriod: 30,
    terminationClause: '',
    
    // Approval & Compliance
    approvalLevel: 'Manager',
    requiresLegalReview: false,
    requiresFinanceReview: false,
    complianceRequirements: [],
    
    // Templates & Workflows
    defaultTemplate: '',
    workflowSteps: [],
    customFields: [],
    
    // Features & Capabilities
    features: [],
    integrations: [],
    reportingRequirements: [],
    
    // Advanced Settings
    tags: [],
    metadata: {},
    isPublic: false,
    allowCustomization: true
  });

  // Tab configuration
  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: <Settings size={16} /> },
    { id: 'pricing', label: 'Pricing Model', icon: <DollarSign size={16} /> },
    { id: 'fixed-price', label: 'Fixed-Price Config', icon: <Target size={16} /> },
    { id: 'time-materials', label: 'T&M Config', icon: <Clock size={16} /> },
    { id: 'workflow', label: 'Workflow', icon: <Workflow size={16} /> },
    { id: 'templates', label: 'Templates', icon: <FileText size={16} /> },
    { id: 'compliance', label: 'Compliance', icon: <Shield size={16} /> },
    { id: 'advanced', label: 'Advanced', icon: <Zap size={16} /> }
  ];

  // Load contract type data
  useEffect(() => {
    if (contractTypeId) {
      loadContractType();
    }
  }, [contractTypeId]);

  const loadContractType = async () => {
    if (!contractTypeId) {
      console.error('No contract type ID provided');
      return;
    }
    
    setLoading(true);
    try {
      // Load contract type from backend
      const response = await MasterDataService.getById(contractTypeId);
      const item = response.data || response;
      
      if (!item) {
        throw new Error('Contract type not found');
      }
      
      // Extract metadata
      let metadata = {};
      if (item.metadata) {
        if (item.metadata instanceof Map) {
          metadata = Object.fromEntries(item.metadata);
        } else if (typeof item.metadata === 'object') {
          metadata = item.metadata;
        }
      }
      
      // Transform backend data to form data
      const transformedData = {
        id: item._id || item.id || contractTypeId,
        name: item.label || item.code || `Contract Type ${String(item._id || item.id || contractTypeId).substring(String(item._id || item.id || contractTypeId).length - 8)}`,
        description: item.description || '',
        category: metadata.category || item.parentValue || item.parentCategory || 'Other',
        type: item.code || metadata.type || '',
        status: item.isActive !== false ? 'active' : 'inactive',
        priority: metadata.priority || 'Medium',
        pricingModel: metadata.pricingModel || 'Fixed-Price',
        baseAmount: metadata.baseAmount || 0,
        currency: metadata.currency || 'USD',
        hourlyRate: metadata.hourlyRate || 0,
        materialMarkup: metadata.materialMarkup || 0,
        billingFrequency: metadata.billingFrequency || 'Monthly',
        fixedPrice: metadata.fixedPrice || {
          scope: [],
          deliverables: [],
          milestones: [],
          qualityGates: [],
          changeManagement: {
            enabled: true,
            approvalRequired: true,
            maxChanges: 5,
            changeFee: 0
          },
          performanceMetrics: {
            qualityScore: 0,
            timelinessScore: 0,
            customerSatisfaction: 0
          }
        },
        timeMaterials: metadata.timeMaterials || {
          hourlyRates: [],
          materialCosts: [],
          timeTracking: {
            enabled: true,
            required: true,
            approvalRequired: true,
            maxHoursPerDay: 8,
            overtimeAllowed: false
          },
          costTracking: {
            enabled: true,
            categories: [],
            budgetLimits: [],
            costCeiling: 0
          },
          billingRules: {
            minimumHours: 0,
            roundingRules: 'quarter-hour',
            travelTime: false,
            administrativeTime: false
          }
        },
        duration: metadata.duration || '',
        autoRenewal: metadata.autoRenewal || false,
        renewalTerms: metadata.renewalTerms || '',
        noticePeriod: metadata.noticePeriod || 30,
        terminationClause: metadata.terminationClause || '',
        approvalLevel: metadata.approvalLevel || 'Manager',
        requiresLegalReview: metadata.requiresLegalReview || false,
        requiresFinanceReview: metadata.requiresFinanceReview || false,
        complianceRequirements: Array.isArray(metadata.complianceRequirements) ? metadata.complianceRequirements : [],
        workflowSteps: Array.isArray(metadata.workflowSteps) ? metadata.workflowSteps : [],
        features: Array.isArray(metadata.features) ? metadata.features : [],
        integrations: Array.isArray(metadata.integrations) ? metadata.integrations : [],
        reportingRequirements: Array.isArray(metadata.reportingRequirements) ? metadata.reportingRequirements : [],
        tags: Array.isArray(metadata.tags) ? metadata.tags : []
      };
      
      setFormData(transformedData);
    } catch (error) {
      console.error('Error loading contract type:', error);
      showError(error.message || 'Failed to load contract type');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (isViewMode) {
      showError('Cannot save in view mode');
      return;
    }
    
    setSaving(true);
    try {
      if (!formData.name || !formData.name.trim()) {
        showError('Contract type name is required');
        setSaving(false);
        return;
      }
      
      // Build payload similar to CreateContractType
      const formatCode = (value) => {
        if (!value) return '';
        return value
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '');
      };
      
      const code = formatCode(formData.name) || `CONTRACTTYPE_${Date.now()}`;
      const metadata = {
        type: formData.type,
        category: formData.category,
        priority: formData.priority,
        pricingModel: formData.pricingModel,
        baseAmount: parseFloat(formData.baseAmount) || 0,
        currency: formData.currency,
        hourlyRate: parseFloat(formData.hourlyRate) || 0,
        materialMarkup: parseFloat(formData.materialMarkup) || 0,
        billingFrequency: formData.billingFrequency,
        fixedPrice: formData.fixedPrice,
        timeMaterials: formData.timeMaterials,
        duration: formData.duration,
        autoRenewal: formData.autoRenewal,
        renewalTerms: formData.renewalTerms,
        noticePeriod: parseInt(formData.noticePeriod, 10) || 30,
        terminationClause: formData.terminationClause,
        approvalLevel: formData.approvalLevel,
        requiresLegalReview: formData.requiresLegalReview,
        requiresFinanceReview: formData.requiresFinanceReview,
        complianceRequirements: formData.complianceRequirements,
        workflowSteps: formData.workflowSteps,
        features: formData.features,
        integrations: formData.integrations,
        reportingRequirements: formData.reportingRequirements,
        tags: formData.tags
      };

      const payload = {
        category: 'ContractType',
        code,
        label: formData.name.trim(),
        description: formData.description.trim(),
        isActive: formData.status !== 'inactive',
        metadata
      };
      
      // Update existing contract type
      await MasterDataService.update(contractTypeId, payload);
      success('Contract type updated successfully!');
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/contracts/types/management');
      }, 1000);
    } catch (error) {
      console.error('Error saving contract type:', error);
      const message = error.message || error.response?.data?.message || 'Failed to save contract type';
      showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value
      }
    }));
  };

  const addArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], item]
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const renderBasicInfo = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="form-label">Contract Type Name *</label>
          <input
            type="text"
            className="form-input"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Enter contract type name"
          />
        </div>
        
        <div>
          <label className="form-label">Category *</label>
          <select
            className="form-input"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
          >
            <option value="">Select category</option>
            <option value="Software Development">Software Development</option>
            <option value="Consulting">Consulting</option>
            <option value="Professional Services">Professional Services</option>
            <option value="Equipment Lease">Equipment Lease</option>
            <option value="Maintenance">Maintenance</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      <div>
        <label className="form-label">Description *</label>
        <textarea
          className="form-textarea"
          rows={4}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Describe the contract type and its purpose"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="form-label">Type *</label>
          <select
            className="form-input"
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
          >
            <option value="">Select type</option>
            <option value="Fixed-Price">Fixed-Price</option>
            <option value="Time & Materials">Time & Materials</option>
            <option value="Hybrid">Hybrid</option>
          </select>
        </div>

        <div>
          <label className="form-label">Status</label>
          <select
            className="form-input"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="draft">Draft</option>
          </select>
        </div>

        <div>
          <label className="form-label">Priority</label>
          <select
            className="form-input"
            value={formData.priority}
            onChange={(e) => handleInputChange('priority', e.target.value)}
          >
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderPricingModel = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="form-label">Pricing Model *</label>
          <select
            className="form-input"
            value={formData.pricingModel}
            onChange={(e) => handleInputChange('pricingModel', e.target.value)}
          >
            <option value="Fixed-Price">Fixed-Price</option>
            <option value="Time & Materials">Time & Materials</option>
            <option value="Hybrid">Hybrid</option>
          </select>
        </div>

        <div>
          <label className="form-label">Currency</label>
          <select
            className="form-input"
            value={formData.currency}
            onChange={(e) => handleInputChange('currency', e.target.value)}
          >
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
            <option value="CAD">CAD</option>
          </select>
        </div>
      </div>

      {formData.pricingModel === 'Fixed-Price' && (
        <div>
          <label className="form-label">Base Amount</label>
          <input
            type="number"
            className="form-input"
            value={formData.baseAmount}
            onChange={(e) => handleInputChange('baseAmount', e.target.value)}
            placeholder="Enter base amount"
          />
        </div>
      )}

      {formData.pricingModel === 'Time & Materials' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">Default Hourly Rate</label>
            <input
              type="number"
              className="form-input"
              value={formData.hourlyRate}
              onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
              placeholder="Enter hourly rate"
            />
          </div>

          <div>
            <label className="form-label">Material Markup (%)</label>
            <input
              type="number"
              className="form-input"
              value={formData.materialMarkup}
              onChange={(e) => handleInputChange('materialMarkup', e.target.value)}
              placeholder="Enter markup percentage"
            />
          </div>
        </div>
      )}

      <div>
        <label className="form-label">Billing Frequency</label>
        <select
          className="form-input"
          value={formData.billingFrequency}
          onChange={(e) => handleInputChange('billingFrequency', e.target.value)}
        >
          <option value="Weekly">Weekly</option>
          <option value="Monthly">Monthly</option>
          <option value="Quarterly">Quarterly</option>
          <option value="Milestone">Milestone</option>
        </select>
      </div>
    </div>
  );

  const renderFixedPriceConfig = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Scope Definition</h3>
        <div className="space-y-3">
          {formData.fixedPrice.scope.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <input
                type="text"
                className="form-input flex-1"
                value={item}
                onChange={(e) => {
                  const newScope = [...formData.fixedPrice.scope];
                  newScope[index] = e.target.value;
                  handleNestedInputChange('fixedPrice', 'scope', newScope);
                }}
                placeholder="Enter scope item"
              />
              <button
                type="button"
                onClick={() => removeArrayItem('fixedPrice.scope', index)}
                className="btn-modern-danger"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayItem('fixedPrice.scope', '')}
            className="btn-modern-outline"
          >
            <Plus size={16} /> Add Scope Item
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Deliverables</h3>
        <div className="space-y-3">
          {formData.fixedPrice.deliverables.map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <input
                type="text"
                className="form-input flex-1"
                value={item}
                onChange={(e) => {
                  const newDeliverables = [...formData.fixedPrice.deliverables];
                  newDeliverables[index] = e.target.value;
                  handleNestedInputChange('fixedPrice', 'deliverables', newDeliverables);
                }}
                placeholder="Enter deliverable"
              />
              <button
                type="button"
                onClick={() => removeArrayItem('fixedPrice.deliverables', index)}
                className="btn-modern-danger"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayItem('fixedPrice.deliverables', '')}
            className="btn-modern-outline"
          >
            <Plus size={16} /> Add Deliverable
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Milestones</h3>
        <div className="space-y-4">
          {formData.fixedPrice.milestones.map((milestone, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Milestone Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={milestone.name}
                    onChange={(e) => {
                      const newMilestones = [...formData.fixedPrice.milestones];
                      newMilestones[index] = { ...milestone, name: e.target.value };
                      handleNestedInputChange('fixedPrice', 'milestones', newMilestones);
                    }}
                    placeholder="Enter milestone name"
                  />
                </div>
                <div>
                  <label className="form-label">Percentage (%)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={milestone.percentage}
                    onChange={(e) => {
                      const newMilestones = [...formData.fixedPrice.milestones];
                      newMilestones[index] = { ...milestone, percentage: parseInt(e.target.value) };
                      handleNestedInputChange('fixedPrice', 'milestones', newMilestones);
                    }}
                    placeholder="Enter percentage"
                  />
                </div>
                <div>
                  <label className="form-label">Value</label>
                  <input
                    type="number"
                    className="form-input"
                    value={milestone.value}
                    onChange={(e) => {
                      const newMilestones = [...formData.fixedPrice.milestones];
                      newMilestones[index] = { ...milestone, value: parseInt(e.target.value) };
                      handleNestedInputChange('fixedPrice', 'milestones', newMilestones);
                    }}
                    placeholder="Enter value"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeArrayItem('fixedPrice.milestones', index)}
                className="btn-modern-danger mt-2"
              >
                <Trash2 size={16} /> Remove Milestone
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayItem('fixedPrice.milestones', { name: '', percentage: 0, value: 0 })}
            className="btn-modern-outline"
          >
            <Plus size={16} /> Add Milestone
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Change Management</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.fixedPrice.changeManagement.enabled}
                onChange={(e) => handleNestedInputChange('fixedPrice.changeManagement', 'enabled', e.target.checked)}
              />
              Enable Change Management
            </label>
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.fixedPrice.changeManagement.approvalRequired}
                onChange={(e) => handleNestedInputChange('fixedPrice.changeManagement', 'approvalRequired', e.target.checked)}
              />
              Approval Required
            </label>
          </div>
          <div>
            <label className="form-label">Max Changes Allowed</label>
            <input
              type="number"
              className="form-input"
              value={formData.fixedPrice.changeManagement.maxChanges}
              onChange={(e) => handleNestedInputChange('fixedPrice.changeManagement', 'maxChanges', parseInt(e.target.value))}
            />
          </div>
          <div>
            <label className="form-label">Change Fee</label>
            <input
              type="number"
              className="form-input"
              value={formData.fixedPrice.changeManagement.changeFee}
              onChange={(e) => handleNestedInputChange('fixedPrice.changeManagement', 'changeFee', parseInt(e.target.value))}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTimeMaterialsConfig = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Hourly Rates</h3>
        <div className="space-y-3">
          {formData.timeMaterials.hourlyRates.map((rate, index) => (
            <div key={index} className="flex items-center gap-3">
              <input
                type="text"
                className="form-input flex-1"
                value={rate.role}
                onChange={(e) => {
                  const newRates = [...formData.timeMaterials.hourlyRates];
                  newRates[index] = { ...rate, role: e.target.value };
                  handleNestedInputChange('timeMaterials', 'hourlyRates', newRates);
                }}
                placeholder="Role/Position"
              />
              <input
                type="number"
                className="form-input w-32"
                value={rate.rate}
                onChange={(e) => {
                  const newRates = [...formData.timeMaterials.hourlyRates];
                  newRates[index] = { ...rate, rate: parseInt(e.target.value) };
                  handleNestedInputChange('timeMaterials', 'hourlyRates', newRates);
                }}
                placeholder="Rate"
              />
              <button
                type="button"
                onClick={() => removeArrayItem('timeMaterials.hourlyRates', index)}
                className="btn-modern-danger"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayItem('timeMaterials.hourlyRates', { role: '', rate: 0 })}
            className="btn-modern-outline"
          >
            <Plus size={16} /> Add Hourly Rate
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Material Costs</h3>
        <div className="space-y-3">
          {formData.timeMaterials.materialCosts.map((cost, index) => (
            <div key={index} className="flex items-center gap-3">
              <input
                type="text"
                className="form-input flex-1"
                value={cost.category}
                onChange={(e) => {
                  const newCosts = [...formData.timeMaterials.materialCosts];
                  newCosts[index] = { ...cost, category: e.target.value };
                  handleNestedInputChange('timeMaterials', 'materialCosts', newCosts);
                }}
                placeholder="Category"
              />
              <input
                type="number"
                className="form-input w-32"
                value={cost.markup}
                onChange={(e) => {
                  const newCosts = [...formData.timeMaterials.materialCosts];
                  newCosts[index] = { ...cost, markup: parseInt(e.target.value) };
                  handleNestedInputChange('timeMaterials', 'materialCosts', newCosts);
                }}
                placeholder="Markup %"
              />
              <button
                type="button"
                onClick={() => removeArrayItem('timeMaterials.materialCosts', index)}
                className="btn-modern-danger"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayItem('timeMaterials.materialCosts', { category: '', markup: 0 })}
            className="btn-modern-outline"
          >
            <Plus size={16} /> Add Material Cost
          </button>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Time Tracking Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.timeMaterials.timeTracking.enabled}
                onChange={(e) => handleNestedInputChange('timeMaterials.timeTracking', 'enabled', e.target.checked)}
              />
              Enable Time Tracking
            </label>
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.timeMaterials.timeTracking.required}
                onChange={(e) => handleNestedInputChange('timeMaterials.timeTracking', 'required', e.target.checked)}
              />
              Time Tracking Required
            </label>
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.timeMaterials.timeTracking.approvalRequired}
                onChange={(e) => handleNestedInputChange('timeMaterials.timeTracking', 'approvalRequired', e.target.checked)}
              />
              Approval Required
            </label>
          </div>
          <div>
            <label className="form-label">Max Hours Per Day</label>
            <input
              type="number"
              className="form-input"
              value={formData.timeMaterials.timeTracking.maxHoursPerDay}
              onChange={(e) => handleNestedInputChange('timeMaterials.timeTracking', 'maxHoursPerDay', parseInt(e.target.value))}
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Billing Rules</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="form-label">Minimum Hours</label>
            <input
              type="number"
              step="0.25"
              className="form-input"
              value={formData.timeMaterials.billingRules.minimumHours}
              onChange={(e) => handleNestedInputChange('timeMaterials.billingRules', 'minimumHours', parseFloat(e.target.value))}
            />
          </div>
          <div>
            <label className="form-label">Rounding Rules</label>
            <select
              className="form-input"
              value={formData.timeMaterials.billingRules.roundingRules}
              onChange={(e) => handleNestedInputChange('timeMaterials.billingRules', 'roundingRules', e.target.value)}
            >
              <option value="quarter-hour">Quarter Hour</option>
              <option value="half-hour">Half Hour</option>
              <option value="hour">Full Hour</option>
            </select>
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.timeMaterials.billingRules.travelTime}
                onChange={(e) => handleNestedInputChange('timeMaterials.billingRules', 'travelTime', e.target.checked)}
              />
              Include Travel Time
            </label>
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.timeMaterials.billingRules.administrativeTime}
                onChange={(e) => handleNestedInputChange('timeMaterials.billingRules', 'administrativeTime', e.target.checked)}
              />
              Include Administrative Time
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderWorkflowConfig = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Workflow Steps</h3>
        <div className="space-y-4">
          {formData.workflowSteps.map((step, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Step Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={step.name}
                    onChange={(e) => {
                      const newSteps = [...formData.workflowSteps];
                      newSteps[index] = { ...step, name: e.target.value };
                      handleInputChange('workflowSteps', newSteps);
                    }}
                    placeholder="Enter step name"
                  />
                </div>
                <div>
                  <label className="form-label">Approver Role</label>
                  <select
                    className="form-input"
                    value={step.approverRole}
                    onChange={(e) => {
                      const newSteps = [...formData.workflowSteps];
                      newSteps[index] = { ...step, approverRole: e.target.value };
                      handleInputChange('workflowSteps', newSteps);
                    }}
                  >
                    <option value="Manager">Manager</option>
                    <option value="Director">Director</option>
                    <option value="VP">VP</option>
                    <option value="Legal">Legal</option>
                    <option value="Finance">Finance</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Order</label>
                  <input
                    type="number"
                    className="form-input"
                    value={step.order}
                    onChange={(e) => {
                      const newSteps = [...formData.workflowSteps];
                      newSteps[index] = { ...step, order: parseInt(e.target.value) };
                      handleInputChange('workflowSteps', newSteps);
                    }}
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeArrayItem('workflowSteps', index)}
                className="btn-modern-danger mt-2"
              >
                <Trash2 size={16} /> Remove Step
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => addArrayItem('workflowSteps', { name: '', approverRole: 'Manager', order: formData.workflowSteps.length + 1 })}
            className="btn-modern-outline"
          >
            <Plus size={16} /> Add Workflow Step
          </button>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInfo();
      case 'pricing':
        return renderPricingModel();
      case 'fixed-price':
        return renderFixedPriceConfig();
      case 'time-materials':
        return renderTimeMaterialsConfig();
      case 'workflow':
        return renderWorkflowConfig();
      default:
        return renderBasicInfo();
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/contracts/types/management')}
            className="btn-modern-secondary"
          >
            <ArrowLeft size={16} />
            Back to Contract Types
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">
              {contractTypeId 
                ? (isViewMode ? 'View Contract Type' : 'Edit Contract Type')
                : 'Create Contract Type'}
            </h1>
            <p className="page-subtitle">
              {contractTypeId 
                ? (isViewMode 
                    ? 'View contract type details and configuration' 
                    : 'Configure and customize contract type settings')
                : 'Set up a new contract type with custom configurations'
              }
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {!isViewMode && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-modern-primary"
              >
                {saving ? (
                  <>
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Saving...</span>
                    </div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Configuration
                  </>
                )}
              </button>
            )}
            {isViewMode && (
              <button
                onClick={() => navigate(`/contracts/types/configure/${contractTypeId}?mode=edit`)}
                className="btn-modern-primary"
              >
                <Edit size={16} />
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`page-tabs-item ${activeTab === tab.id ? 'page-tabs-item-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="page-tab-content">
        {renderTabContent()}
      </div>
      
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default ContractTypeConfiguration;
